import { isEqual } from 'lodash'
import queryString from 'query-string'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useHistory, useLocation } from 'react-router'
import { useDebounceValue, useQueryParams } from 'src/hooks'
import { preserveReservedParams } from 'src/navigation/reservedParams'
import { LocationState } from 'src/redux/createRootReducer'

type KeyStr<T> = Extract<keyof T, string>
const VIEW_ID_KEY = 'viewId'

export type UseFilterOptions<T, M> = {
  /** function to translate query params to filters */
  normalizeQueryParams?: (
    queryParams: queryString.ParsedQuery<string>,
  ) => Partial<T>
  /** apply these filters if query params are empty */
  defaultFilters: T
  /** these filters are auto-applied on load but are NOT defaults */
  initialFilters?: T
  /** function to translate filters to api request */
  apiQueryParams: (filters: Partial<T>) => M
  /** function to translate filters to URL */
  stringifyFilters?: (filters: Partial<T>) => string
  /** map of saved filter IDs to full filter objects */
  savedFiltersMap?: Record<string, Partial<T>>
  equalsMap?: Partial<Record<KeyStr<T>, (a: any, b: any) => boolean>>
  /** filter keys to exclude from selectedFiltersCount and changedFilterKeys */
  excludedFilterKeysFromCount?: KeyStr<T>[]
  diff?: (
    defaults: T,
    current: Partial<T>,
  ) => { changedKeys: (keyof T)[]; count: number }
}

type UseFiltersReturnType<T, M> = {
  /** current filters state */
  filters: Partial<T>
  /** serializable object for an API call */
  apiQueryParams: M
  /** to handle updates to individual filters */
  handleFilterChange: (newFilters: Partial<T>) => void
  /** to reset filters to default */
  resetFilters: () => void
  /** to reset a single filter to default */
  resetSingleFilter: (key: keyof T) => void
  /** boolean representing divergence from defaultFilters */
  isDirty: boolean
  /** true when any non-view filter differs from defaults (ignores excludedFilterKeysFromCount) */
  hasViewFiltersChange: boolean
  selectedFiltersCount: number
  changedFilterKeys: (keyof T)[]
}

/**
 * Using this hook updates the URL as filters change
 * Engineers are expected to consume all of the return types and use the handleFilterChange function returned by this hook
 * This hook doesn't make the API call, but prepares a serializable object for an API call dependent
 * on whatever parameters and generics passed in.
 */

/**
 * useFilters<T, M>
 *
 * React hook for managing UI filter state and deriving API request params.
 * It keeps a `filters` state (based on your defaults), updates it via helpers,
 * and exposes a derived `apiQueryParams` value suitable for calling your API.
 * Using this hook updates the URL as filters change via history.replace, but
 *
 * @template T Filters shape consumed by your filter UI/components (what the user edits).
 *             This is usually UI-friendly and may include arrays, strings, etc.
 *             Example:
 *             interface ExpenseCategoryFilters {
 *               search?: string;
 *               status?: string[];
 *               required?: string[];
 *             }
 *
 * @template M Request params shape sent to your API (what your backend expects).
 *             This is often renamed/normalized relative to T.
 *             Example:
 *             type ExpenseCategoryFiltersRequestParams = {
 *               search?: string;
 *               active?: boolean;
 *               required?: boolean;
 *               sortField?: ExpenseCategorySortField;
 *               sortDirection?: 'ASC' | 'DESC';
 *             }
 *
 * @param options.normalizeQueryParams Optional function to normalize raw query params
 *                                     from the URL to filters.
 * @param options.defaultFilters Default values for the filters. The keys
 *                               here also define the allowed set of filter keys.
 * @param options.initialFilters Initial values for the filters that are shown on top of the
 *                               defaults. Values defined here are counted towards
 *                               selectedFiltersCount and can be reset.
 * @param options.apiQueryParams Mapping from the current filters (Partial<T>) to M.
 *                               Use this to:
 *                               - rename keys (e.g., `status[]` -> `active?: boolean`)
 *                               - coerce types (arrays -> booleans, strings -> numbers)
 *                               - add derived params (sorting, pagination)
 *                               - drop empty/undefined values before hitting the API
 * @param options.stringifyFilters Optional function to serialize the current filters
 *                                into a query string for the browser URL. Signature:
 *                                `(filters: Partial<T>) => string`.
 *                                Defaults to `queryString.stringify(filters)`.
 *                                Supply your own if you need a specific encoding
 *                                (e.g., array format, skipping empties/nulls).
 *
 * @returns {
 *   filters: Partial<T>;                        // current UI filters
 *   apiQueryParams: M;                          // derived API request params
 *   handleFilterChange: (patch: Partial<T>) => void; // merge+update filters
 *   resetFilters: () => void;                   // reset to defaultFilters
 *   resetSingleFilter: (key: keyof T) => void;  // reset a single filter to default
 *   isDirty: boolean;                           // true if filters differ from defaults
 * }
 *
 * @example
 * type T = ExpenseCategoryFilters;
 * type M = ExpenseCategoryFiltersRequestParams;
 *
 * const { filters, apiQueryParams, handleFilterChange, resetFilters, isDirty } =
 *   useFilters<T, M>({
 *     defaultFilters: {
 *       search: '',
 *       status: [],
 *       required: [],
 *     },
 *     apiQueryParams: (f) => ({
 *       // pass through or drop empties as needed
 *       search: f.search || undefined,
 *       // map array-based UI filters to booleans for the API
 *       active: parseBooleanQueryParamFromArray(f.status ?? [], ExpenseCategoryStatus.Active),
 *       required: parseBooleanQueryParamFromArray(f.required ?? [], RequiredStatus.Required),
 *       // add server-side sorting
 *       sortField: ExpenseCategorySortField.Name,
 *       sortDirection: 'ASC',
 *     }),
 *   });
 *
 * // T (UI) and M (API) are intentionally decoupled:
 * // - T models what the component needs to render and collect input.
 * // - M models what the backend needs to execute the query efficiently.
 */
function useFilters<T, M>(
  options: UseFilterOptions<T, M>,
): UseFiltersReturnType<T, M> {
  const history = useHistory<LocationState>()
  const { pathname, search: currentSearch } = useLocation()
  const rawQueryParams = useQueryParams()

  const allowedKeys = useMemo(
    () => Object.keys(options.defaultFilters || {}) as KeyStr<T>[],
    [options.defaultFilters],
  )

  const queryParams = useMemo(() => {
    if (options.normalizeQueryParams) {
      // If provided, use custom normalization function
      return options.normalizeQueryParams(rawQueryParams)
    } else {
      // Default normalization: filter to allowed keys and coerce types
      // The default normalization handles strings and string arrays
      const filtered = Object.fromEntries(
        Object.entries(rawQueryParams).filter(([key]) =>
          allowedKeys.includes(key as KeyStr<T>),
        ),
      )
      // Normalize based on default filter types
      const normalized = Object.fromEntries(
        Object.entries(filtered).map(([key, value]) => {
          const defaultValue = options.defaultFilters?.[key as keyof T]
          // If default is an array, ensure parsed value is also an array
          if (Array.isArray(defaultValue)) {
            return [key, Array.isArray(value) ? value : value ? [value] : []]
          }
          return [key, value]
        }),
      )

      return normalized as Partial<T>
    }
  }, [rawQueryParams, allowedKeys, options.defaultFilters])

  const initialFilters = {
    ...options.defaultFilters,
    ...queryParams,
    ...options.initialFilters,
  }

  const filterToAllowed = useCallback(
    (obj: Partial<T>): Partial<T> =>
      Object.fromEntries(
        Object.entries(obj).filter(([k]) =>
          allowedKeys.includes(k as KeyStr<T>),
        ),
      ) as Partial<T>,
    [allowedKeys],
  )

  const getActiveViewId = useCallback((filters: Partial<T>): string | undefined => {
    const maybeViewId = (filters as Record<string, unknown>)[VIEW_ID_KEY]
    return typeof maybeViewId === 'string' && maybeViewId.length > 0
      ? maybeViewId
      : undefined
  }, [])

  const applySavedFiltersByViewId = useCallback(
    (
      input: Partial<T>,
      optionsOverride?: {
        ignoreDefaultOverrides?: boolean
        explicitKeys?: Set<string>
      },
    ): Partial<T> => {
      const next = filterToAllowed(input)
      const activeViewId = getActiveViewId(next)
      if (!activeViewId || !options.savedFiltersMap) {
        return next
      }

      const savedFilters = options.savedFiltersMap[activeViewId]
      if (!savedFilters) {
        return next
      }

      const baseline = {
        ...options.defaultFilters,
        ...savedFilters,
      } as Partial<T>
      const nextOverrides: Record<string, unknown> = {}
      for (const [key, value] of Object.entries(next)) {
        if (key === VIEW_ID_KEY) continue
        if (optionsOverride?.ignoreDefaultOverrides) {
          const eq = options.equalsMap?.[key as KeyStr<T>]
          const defaultValue = (options.defaultFilters as Record<string, unknown>)[
            key
          ]
          const isDefault = eq
            ? eq(defaultValue, value)
            : isEqual(defaultValue, value)
          if (isDefault && !optionsOverride.explicitKeys?.has(key)) {
            continue
          }
        }
        nextOverrides[key] = value
      }

      return filterToAllowed({
        ...baseline,
        ...nextOverrides,
        [VIEW_ID_KEY]: activeViewId,
      } as Partial<T>)
    },
    [
      filterToAllowed,
      getActiveViewId,
      options.savedFiltersMap,
      options.defaultFilters,
      options.equalsMap,
    ],
  )

  const explicitQueryKeys = useMemo(
    () =>
      new Set(
        Object.keys(rawQueryParams).filter((key) =>
          allowedKeys.includes(key as KeyStr<T>),
        ),
      ),
    [rawQueryParams, allowedKeys],
  )

  const getViewAwareUrlFilters = useCallback(
    (filters: Partial<T>): Partial<T> => {
      const activeViewId = getActiveViewId(filters)
      if (!activeViewId || !options.savedFiltersMap?.[activeViewId]) {
        return filters
      }

      const baseline = applySavedFiltersByViewId(
        { [VIEW_ID_KEY]: activeViewId } as unknown as Partial<T>,
      )
      const overrides: Record<string, unknown> = {}
      for (const [key, value] of Object.entries(filters)) {
        if (key === VIEW_ID_KEY) continue
        const eq = options.equalsMap?.[key as KeyStr<T>]
        const baseValue = (baseline as Record<string, unknown>)[key]
        const isSame = eq ? eq(baseValue, value) : isEqual(baseValue, value)
        if (!isSame) {
          overrides[key] = value
        }
      }

      return filterToAllowed({
        ...overrides,
        [VIEW_ID_KEY]: activeViewId,
      } as unknown as Partial<T>)
    },
    [
      getActiveViewId,
      options.savedFiltersMap,
      options.equalsMap,
      applySavedFiltersByViewId,
      filterToAllowed,
    ],
  )

  const resolvedInitialFilters = useMemo(
    () =>
      applySavedFiltersByViewId(initialFilters, {
        ignoreDefaultOverrides: true,
        explicitKeys: explicitQueryKeys,
      }),
    [applySavedFiltersByViewId, initialFilters, explicitQueryKeys],
  )

  const [currentFilters, setCurrentFilters] =
    useState<Partial<T>>(resolvedInitialFilters)

  const updateCurrentUrl = useCallback(
    (filters: Partial<T>): void => {
      const cleaned = Object.fromEntries(
        Object.entries(filters).filter(([, value]) => {
          if (value === '' || value === undefined || value === null)
            return false
          if (Array.isArray(value) && value.length === 0) return false
          return true
        }),
      ) as Partial<T>
      const filtersForUrl = getViewAwareUrlFilters(cleaned)
      const activeViewId = getActiveViewId(filtersForUrl)

      let stringified = ''
      if (activeViewId) {
        stringified = queryString.stringify(
          { [VIEW_ID_KEY]: activeViewId } as Record<string, string>,
          {
            skipNull: true,
            skipEmptyString: true,
            arrayFormat: 'none',
          },
        )
      } else {
        stringified = options.stringifyFilters
          ? options.stringifyFilters(filtersForUrl)
          : queryString.stringify(filtersForUrl, {
              skipNull: true,
              skipEmptyString: true,
              arrayFormat: 'none',
            })
      }

      const nextSearch = stringified ? `?${stringified}` : ''
      if (nextSearch !== currentSearch) {
        // avoid no-op replaces; preserve any open overlay (modal*/flyout*) params
        // the filter rebuild would otherwise strip (no-op when none are open).
        history.replace({
          pathname,
          search: preserveReservedParams(currentSearch, nextSearch),
        })
      }
    },
    [
      history,
      pathname,
      currentSearch,
      options.stringifyFilters,
      getActiveViewId,
      getViewAwareUrlFilters,
    ],
  )

  type FiltersUpdater = Partial<T> | ((prev: Partial<T>) => Partial<T>)

  const updateFilters = useCallback(
    (updater: FiltersUpdater): void => {
      setCurrentFilters((prev) => {
        const nextRaw =
          typeof updater === 'function'
            ? (updater as (p: Partial<T>) => Partial<T>)(prev)
            : updater

        const next = applySavedFiltersByViewId(nextRaw)
        // Always sync the URL, even if state object is unchanged.
        // updateCurrentUrl itself no-ops if the search string wouldn't change.
        updateCurrentUrl(next)

        // Avoid unnecessary setState re-render if identical.
        return isEqual(prev, next) ? prev : next
      })
    },
    [applySavedFiltersByViewId, updateCurrentUrl],
  )

  const handleFilterChange = useCallback(
    (patch: Partial<T>) => {
      updateFilters((prev) => {
        const hasViewIdInPatch = Object.prototype.hasOwnProperty.call(
          patch,
          VIEW_ID_KEY,
        )
        const nextRecord: Record<string, unknown> = hasViewIdInPatch
          ? { ...patch }
          : { ...prev, ...patch }
        if (
          !hasViewIdInPatch &&
          getActiveViewId(prev) &&
          options.savedFiltersMap
        ) {
          nextRecord[VIEW_ID_KEY] = undefined
        }
        return nextRecord as Partial<T>
      })
    },
    [updateFilters, getActiveViewId, options.savedFiltersMap],
  )

  const resetFilters = useCallback((): void => {
    updateFilters(options.defaultFilters)
  }, [updateFilters, options.defaultFilters])

  const resetSingleFilter = useCallback(
    (key: keyof T): void => {
      updateFilters((prev) => {
        const nextRecord: Record<string, unknown> = {
          ...prev,
          [key]: options.defaultFilters[key],
        }
        if (
          key !== (VIEW_ID_KEY as keyof T) &&
          getActiveViewId(prev) &&
          options.savedFiltersMap
        ) {
          nextRecord[VIEW_ID_KEY] = undefined
        }
        return nextRecord as Partial<T>
      })
    },
    [updateFilters, options.defaultFilters, getActiveViewId, options.savedFiltersMap],
  )

  useEffect(() => {
    const activeViewId = getActiveViewId(currentFilters)
    if (!activeViewId || !options.savedFiltersMap?.[activeViewId]) {
      return
    }

    const resolvedFilters = applySavedFiltersByViewId(currentFilters, {
      ignoreDefaultOverrides: true,
      explicitKeys: explicitQueryKeys,
    })
    if (!isEqual(currentFilters, resolvedFilters)) {
      setCurrentFilters(resolvedFilters)
    }
  }, [
    currentFilters,
    getActiveViewId,
    options.savedFiltersMap,
    applySavedFiltersByViewId,
    explicitQueryKeys,
  ])

  const apiQueryParams = useMemo(
    () => options.apiQueryParams(currentFilters),
    [options.apiQueryParams, currentFilters],
  )

  const debouncedApiParamsJson = useDebounceValue(
    useMemo(() => JSON.stringify(apiQueryParams), [apiQueryParams]),
    300,
  )
  const debouncedApiQueryParams = useMemo(
    () => JSON.parse(debouncedApiParamsJson),
    [debouncedApiParamsJson],
  )

  const { changedKeys, count, hasViewFiltersChange } = useMemo(() => {
    const raw = (() => {
      if (options.diff)
        return options.diff(options.defaultFilters, currentFilters)
      const changed: (keyof T)[] = []
      for (const k of allowedKeys) {
        const eq = options.equalsMap?.[k as KeyStr<T>]
        const a = (options.defaultFilters as any)[k]
        const b = (currentFilters as any)[k]
        const equal = eq ? eq(a, b) : isEqual(a, b)
        if (!equal) changed.push(k as keyof T)
      }
      return { changedKeys: changed, count: changed.length }
    })()

    const changedKeysExcludingViewId = raw.changedKeys.filter(
      (key) => (key as string) !== VIEW_ID_KEY,
    )

    const excludedKeys = new Set<string>([
      VIEW_ID_KEY,
      ...((options.excludedFilterKeysFromCount as string[] | undefined) ?? []),
    ])
    const filteredChangedKeys = changedKeysExcludingViewId.filter(
      (key) => !excludedKeys.has(key as string),
    )
    return {
      changedKeys: filteredChangedKeys,
      count: filteredChangedKeys.length,
      hasViewFiltersChange: changedKeysExcludingViewId.length > 0,
    }
  }, [
    options.diff,
    options.defaultFilters,
    currentFilters,
    allowedKeys,
    options.equalsMap,
    options.excludedFilterKeysFromCount,
  ])

  return {
    filters: currentFilters,
    apiQueryParams: debouncedApiQueryParams,
    handleFilterChange: handleFilterChange,
    resetFilters: resetFilters,
    resetSingleFilter: resetSingleFilter,
    isDirty: count > 0,
    hasViewFiltersChange: hasViewFiltersChange,
    selectedFiltersCount: count,
    changedFilterKeys: changedKeys,
  }
}

export default useFilters
